/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { UserCompany } from '../types';
import { useAppStore } from '@/shared/store/useAppStore';

export interface CompanyContextValue {
  companyId: string;
  currentCompany: UserCompany | undefined;
  userCompanies: UserCompany[];
  canAccessMultiple: boolean;
  switchCompany: (nextId: string) => void;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

interface CompanyProviderProps {
  companyId: string;
  userCompanies: UserCompany[];
  children: ReactNode;
}

export const CompanyProvider = ({ companyId, userCompanies, children }: CompanyProviderProps) => {
  const setActiveCompanyId = useAppStore((state) => state.setActiveCompanyId);

  const currentCompany = useMemo(
    () => userCompanies.find((c) => c.id === companyId),
    [companyId, userCompanies]
  );

  const switchCompany = useCallback(
    (nextId: string) => {
      if (!nextId || nextId === companyId) return;
      setActiveCompanyId(nextId);
    },
    [companyId, setActiveCompanyId]
  );

  const value = useMemo<CompanyContextValue>(
    () => ({
      companyId,
      currentCompany,
      userCompanies,
      canAccessMultiple: userCompanies.length > 1,
      switchCompany,
    }),
    [companyId, currentCompany, userCompanies, switchCompany]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export const useCurrentCompany = (): CompanyContextValue => {
  const value = useContext(CompanyContext);
  if (!value) {
    throw new Error('useCurrentCompany must be used inside <CompanyProvider>');
  }
  return value;
};

// Safe variant for code that may run outside the CompanyShell (e.g. /companies
// landing page, auth flow). Returns null if there is no active company.
export const useCurrentCompanyOptional = (): CompanyContextValue | null => {
  return useContext(CompanyContext);
};
